import { getPlayerStats } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useState, useMemo } from "react";
import { format, subDays, parseISO, subHours } from "date-fns";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useTranslation } from "react-i18next";
import { formatToUTC } from "@/lib/dateUtils";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  playerId: number;
}

export const PlayerStatsChart = ({ playerId }: Props) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [timeRange, setTimeRange] = useState<"1h" | "1d" | "7d" | "30d">("1d");

  // Calculate date ranges based on selected time range
  const now = new Date();

  // Format dates for display and create ISO strings for API
  const getFormattedDates = useMemo(() => {
    let startDateObj;

    switch (timeRange) {
      case "1h":
        // For 1h, ensure we get exactly 1 hour of data with precise minutes
        startDateObj = subHours(now, 1);
        // Set seconds to 0 to ensure consistent time ranges
        startDateObj.setSeconds(0);
        now.setSeconds(0);
        break;
      case "1d":
        startDateObj = subDays(now, 1);
        break;
      case "7d":
        startDateObj = subDays(now, 7);
        break;
      case "30d":
        startDateObj = subDays(now, 30);
        break;
      default:
        startDateObj = subDays(now, 1);
    }

    // Format dates in the format expected by formatToUTC (dd-MM-yyyy HH:mm)
    const startDateFormatted = format(startDateObj, "dd-MM-yyyy HH:mm");
    const endDateFormatted = format(now, "dd-MM-yyyy HH:mm");

    // Convert to UTC for API
    const startDateUTC = formatToUTC(startDateFormatted);
    const endDateUTC = formatToUTC(endDateFormatted);

    return { startDateUTC, endDateUTC };
  }, [timeRange]);

  const { data: playerStats, isLoading } = useQuery({
    queryKey: ["getPlayerStats", playerId, getFormattedDates.startDateUTC, getFormattedDates.endDateUTC],
    queryFn: () =>
      getPlayerStats(
        playerId,
        String(activeFirm?.firmGuid),
        getFormattedDates.startDateUTC,
        getFormattedDates.endDateUTC
      ),
  });

  const playerStatsData = playerStats?.data || [];

  // Process data for the chart
  const chartData = useMemo(() => {
    // Group stats by hour or day depending on the time range
    const groupedData = new Map();

    playerStatsData.forEach((stat) => {
      const date = parseISO(stat.createdAt);
      let timeKey;

      if (timeRange === "1h") {
        // Group by minutes for 1 hour view with more granularity
        timeKey = format(date, "HH:mm");
      } else if (timeRange === "1d") {
        // Group by hour for 1 day view
        timeKey = format(date, "HH:mm");
      } else if (timeRange === "7d") {
        // Group by day and hour for 7 day view
        timeKey = format(date, "MM-dd HH:mm");
      } else {
        // Group by day for 30 day view
        timeKey = format(date, "MM-dd");
      }

      if (!groupedData.has(timeKey)) {
        groupedData.set(timeKey, { time: timeKey, count: 0 });
      }

      const entry = groupedData.get(timeKey);
      entry.count += 1;
    });

    // Convert map to array and sort by time
    return Array.from(groupedData.values()).sort((a, b) => {
      return a.time.localeCompare(b.time);
    });
  }, [playerStatsData, timeRange]);

  const chartConfig = {
    count: {
      label: t("player.stats.count"),
      color: "hsl(var(--chart-1))",
    },
  } satisfies ChartConfig;

  return (
    <Card className="flex flex-col border-none shadow-sm bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950/50 dark:to-slate-900/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">{t("player.stats.title")}</CardTitle>
            <CardDescription className="text-xs">{t("player.stats.description")}</CardDescription>
          </div>
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={(value) => value && setTimeRange(value as "1h" | "1d" | "7d" | "30d")}
          >
            <ToggleGroupItem value="1h">{t("player.stats.timeRanges.1h")}</ToggleGroupItem>
            <ToggleGroupItem value="1d">{t("player.stats.timeRanges.1d")}</ToggleGroupItem>
            <ToggleGroupItem value="7d">{t("player.stats.timeRanges.7d")}</ToggleGroupItem>
            <ToggleGroupItem value="30d">{t("player.stats.timeRanges.30d")}</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        {isLoading ? (
          <div className="flex h-[250px] items-center justify-center">
            <LoadingSmall />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-[250px] items-center justify-center">
            <p className="text-sm text-muted-foreground">{t("player.stats.noData")}</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <ChartContainer config={chartConfig}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    // Simplify labels based on time range
                    if (timeRange === "30d" && chartData.length > 15) {
                      // Only show every 3rd label for 30d to avoid crowding
                      const idx = chartData.findIndex((item) => item.time === value);
                      return idx % 3 === 0 ? value : "";
                    }
                    if (timeRange === "1h" && chartData.length > 10) {
                      // For 1h view, only show every 5 minutes to avoid crowding
                      const idx = chartData.findIndex((item) => item.time === value);
                      return idx % 2 === 0 ? value : "";
                    }
                    return value;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => value.toLocaleString()} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => `${value}`}
                      formatter={(value) => [`${value}`, t("player.stats.views")]}
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  name={t("player.stats.views")}
                  stroke="var(--color-count)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Legend />
              </LineChart>
            </ChartContainer>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
